import React, { PureComponent, Fragment } from 'react';
import 'url-search-params-polyfill';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { fieldTypes } from '@actions/conference';
import { personType } from '@reducers/conference';
import rulesCodes from '@config/rules-constants';
import _ from 'lodash';
import styled from 'styled-components';
import { FlexContainer, Whitespace } from '@layout';
import { FileUpload, H2, CheckboxBlock, Icon, Dropdown } from '@atoms';
import {
  FileList,
  TimeManager,
  ChipsInput,
  Pin,
  TextInput,
  IntroMessagesDropdown,
  LanguageDropdown,
} from '@molecules';
import { People, ChangeAnalyst } from '@organisms';
import messages from './messages';

const StyledLabel = styled.div`
  box-sizing: border-box;
  color: rgb(175, 175, 175);
  cursor: default;
  display: block;
  font-family: Lato, sans-serif;
  font-size: 12px;
  padding-top: 3px;
`;

const StyledError = styled.div`
  box-sizing: border-box;
  color: rgb(175, 0, 0);
  cursor: default;
  display: block;
  font-family: Lato, sans-serif;
  font-size: 9px;
  padding-top: 3px;
`;

class ConferenceCallInformationForm extends PureComponent {
  componentDidMount() {
    this.props.loadOrganizationsList();
    this.props.getAvailableLanguagesList();
    this.props.getIntroMessagesList({ include: 'organization' });
  }

  mapFileNames = (files) => files.map((file) => file.name);

  getCallAudioFiles = () => (this.props.callAudio ? [this.props.callAudio] : []);

  getAudioDuration = (file) => {
    const blobUrl = URL.createObjectURL(file);
    const audioTag = document.createElement('audio');
    audioTag.src = blobUrl;
    return new Promise((resolve, reject) => {
      audioTag.addEventListener('loadeddata', () => resolve(audioTag.duration));
      audioTag.addEventListener('error', () => reject("Can't get audio-file duration"));
    });
  };

  setCallAudioFile = async (files) => {
    const [audioFile] = files;
    try {
      const duration = await this.getAudioDuration(audioFile);
      Object.assign(audioFile, { duration });
    } catch (err) {
      console.warn(err);
    }
    return this.props.setCallAudio(audioFile);
  };

  handleTitleChange = ({ target: { value } }) => {
    this.props.setField(fieldTypes.TITLE, value);
  };

  handleClientReferenceChange = ({ target: { value } }) => {
    value.length <= 10 && this.props.setField(fieldTypes.CLIENT_REFERENCE, value);
  };

  handleProjectCodeChange = ({ target: { value } }) => {
    this.props.setField(fieldTypes.PROJECT_CODE, value);
  };

  handleSpecialistNumberChange = ({ target: { value } }) => {
    this.props.setField(fieldTypes.SPECIALIST_NUMBER, value);
  };

  handleCheckCreateTranscript = (e, value) => {
    const {
      isTranscriptAvailableLanguageRule,
      isTranscriptRequiredRule,
    } = this.processTrscRules();

    if (isTranscriptRequiredRule && !value) {
      return;
    }

    if (!isTranscriptAvailableLanguageRule && value) {
      value = false;
      alert('Transcription mechanism is not supported for the chosen language');
    }

    this.props.setField(fieldTypes.CREATE_TRANSCRIPT, value);
  };

  handleCheckCreateAITranscript = (e, value) => {
    const {
      isAITranscriptAvailableLanguageRule,
      isAITranscriptRequiredRule,
    } = this.processTrscRules();

    if (isAITranscriptRequiredRule && !value) {
      return;
    }

    if (!isAITranscriptAvailableLanguageRule && value) {
      value = false;
      alert('Transcription mechanism is not supported for the chosen language');
    }

    this.props.setField(fieldTypes.CREATE_AI_TRANSCRIPT, value);
  };

  handleUsingTwoFactorAuth = (e, value) => {
    this.props.setField(fieldTypes.USE_TWO_FACTOR_AUTH, value);
  };

  changeAnalyst = (analystId) => {
    this.props.setField(fieldTypes.ANALYST, +analystId);
  };

  changeIntroMessage = (event) => {
    const { value } = event.target;
    this.props.setField(fieldTypes.INTRO_MESSAGE, value);
  };

  changeLanguage = (event) => {
    const { value } = event.target;
    this.props.changeLanguage({
      language: value,
      languagePrefs: this.languagePrefs(value),
    });
  };

  presetDefaultLanguage() {
    if (!this.props.languagePrefs || this.props.languagePrefs == []) {
      this.props.changeLanguage({
        language: this.props.language,
        languagePrefs: this.languagePrefs(this.props.language),
      });
    }
  }

  changeOrganization = (value) => {
    const { organizationId, organizationsList, introMessagesList } = this.props;

    this.props.setField(fieldTypes.ORGANIZATION, value === '-' ? null : value);
    const currentOrganization = organizationsList.find(
      ({ id }) => String(value) === String(id),
    );
    this.props.setField(fieldTypes.CURRENT_ORGANIZATION, currentOrganization);
  };

  processAndApplyTrscRules() {
    const rules = this.processTrscRules();

    this.errorLangHuman = rules.errorLangHuman;
    this.errorLangAI = rules.errorLangAI;

    return rules;
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps[fieldTypes.ORGANIZATION] !== this.props[fieldTypes.ORGANIZATION] ||
      prevProps[fieldTypes.LANGUAGE] !== this.props[fieldTypes.LANGUAGE]
    ) {
      this.presetDefaultLanguage();
      this.processAndApplyOrganizationRules();
      this.processAndApplyTrscRules();
    }

    if (prevProps.languagesList !== this.props.languagesList) {
      this.presetDefaultLanguage();
    }
  }

  processTrscRules() {
    const {
      language,
      isAITranscriptionEnabled,
      isHumanTranscriptionEnabled,
      isTranscriptNeeded,
      isAITranscriptNeeded,
    } = this.props;

    const {
      isMFARequiredRule,
      isTranscriptRequiredRule,
      isAITranscriptRequiredRule,
      isDownloadCallRecordingsRule,
    } = this.processOrganizationRules();

    const {
      isTranscriptAvailableLanguageRule,
      isAITranscriptAvailableLanguageRule,
    } = this.rulesForLanguage();

    const errorLangHuman =
      (isTranscriptRequiredRule && !isTranscriptAvailableLanguageRule) || false;
    const errorLangAI =
      (isAITranscriptRequiredRule && !isAITranscriptAvailableLanguageRule) || false;

    const isHumanTranscriptionDisabled =
      isTranscriptRequiredRule || !isHumanTranscriptionEnabled;
    const isHumanTranscriptionCheckboxChecked = isTranscriptNeeded;
    const isAITranscriptionDisabled =
      isAITranscriptRequiredRule || !isAITranscriptionEnabled;
    const isAITranscriptionCheckboxChecked = isAITranscriptNeeded;

    const flags = {
      isHumanTranscriptionDisabled,
      isAITranscriptionDisabled,

      isHumanTranscriptionCheckboxChecked,
      isAITranscriptionCheckboxChecked,

      isTranscriptAvailableLanguageRule,
      isAITranscriptAvailableLanguageRule,

      isTranscriptRequiredRule,
      isAITranscriptRequiredRule,

      errorLangHuman,
      errorLangAI,
    };

    return flags;
  }

  processOrganizationRules() {
    const { organizationId, organizationsList } = this.props;
    const resultRules = {
      isMFARequiredRule: false,
    };
    if (!_.isNumber(Number(organizationId)) || !organizationsList.length) {
      return resultRules;
    }
    const currentOrganization = organizationsList.find(
      ({ id }) => String(organizationId) === String(id),
    );
    if (!currentOrganization) {
      return resultRules;
    }

    const { rules = [] } = currentOrganization;
    resultRules.isMFARequiredRule = rules.some(
      ({ code }) => rulesCodes.MULTI_FACTOR_AUTHORIZATION === code,
    );
    resultRules.isDownloadCallRecordingsRule = rules.some(
      ({ code }) => rulesCodes.DOWNLOAD_CALL_RECORDINGS === code,
    );
    resultRules.isTranscriptRequiredRule = rules.some(
      ({ code }) => rulesCodes.HUMAN_TRANSCRIPTION_REQUIRED === code,
    );
    resultRules.isAITranscriptRequiredRule = rules.some(
      ({ code }) => rulesCodes.AI_TRANSCRIPTION_REQUIRED === code,
    );

    return resultRules;
  }

  processAndApplyOrganizationRules() {
    const resultRules = this.processOrganizationRules();
    const { organizationId } = this.props;

    if (organizationId && organizationId != '-') {
      if (resultRules.isTranscriptRequiredRule) {
        this.props.setField(
          fieldTypes.CREATE_TRANSCRIPT,
          resultRules.isTranscriptRequiredRule,
        );
      }
      if (resultRules.isAITranscriptRequiredRule) {
        this.props.setField(
          fieldTypes.CREATE_AI_TRANSCRIPT,
          resultRules.isAITranscriptRequiredRule,
        );
      }
    }

    return resultRules;
  }

  addNoneSelectOption(options) {
    options.unshift({ value: '-', label: '-' });
    return options;
  }

  isTwoFactorAuthSelected = (isMFARequiredRule) => {
    const { isTwoFactorAuthRequired } = this.props;
    return isTwoFactorAuthRequired || isMFARequiredRule;
  };

  selectedLanguage() {
    const { language } = this.props;

    return this.languagePrefs(language);
  }

  languagePrefs(langCode) {
    const { languagesList } = this.props;

    return langCode ? languagesList.filter((i) => i.code === langCode)[0] : null;
  }

  rulesForLanguage() {
    const selectedLanguage = this.selectedLanguage();

    const resultRules = {
      isTranscriptAvailableLanguageRule: _.get(selectedLanguage, 'human'),
      isAITranscriptAvailableLanguageRule: _.get(selectedLanguage, 'ai'),
    };

    return resultRules;
  }

  isTwoFactorAuthDisabled = (organizationRules) => {
    const {
      isMFARequiredRule,
      isTranscriptRequiredRule,
      isAITranscriptRequiredRule,
    } = organizationRules;
    const {
      isTranscriptNeeded,
      isAITranscriptNeeded,
      conferenceHasBeenFinished,
    } = this.props;
    const isTwoFactorAuthEnabled =
      conferenceHasBeenFinished ||
      isTranscriptNeeded ||
      isAITranscriptNeeded ||
      isTranscriptRequiredRule ||
      isAITranscriptRequiredRule;
    return !isTwoFactorAuthEnabled || isMFARequiredRule;
  };

  render() {
    let {
      conferenceHasBeenFinished,
      token,
      title,
      pin,
      regeneratePin,
      isRegenerating,
      tags,
      addTag,
      deleteTag,
      people,
      addPerson,
      setPersonField,
      removePerson,
      numbersByCountries,
      isTranscriptNeeded,
      isAITranscriptNeeded,
      isTwoFactorAuthRequired,
      edit,
      userId,
      introMessageId,
      introMessagesList: introMessagesListOptions,
      clientReference,
      projectCode,
      specialistNumber,
      organizationId,
      organizationsList,
      organization,
      defaultAttendees,
      language,
      isAITranscriptionEnabled,
      isHumanTranscriptionEnabled,
      languagesList,
    } = this.props;

    let introMessageSelectDisabled = false;
    if (organizationId && introMessagesListOptions) {
      const defaultIntroMessage = introMessagesListOptions.find(
        (msg) => msg.organizationId == organizationId,
      );
      if (defaultIntroMessage) {
        introMessageId = defaultIntroMessage.id;
        introMessageSelectDisabled = true;
      }
    }

    const languagesListExt = languagesList
      ? languagesList.map((i) => ({
          ...i,
          name:
            i.name +
            (i.ai && !i.human ? ' (ai-only)' : '') +
            (!i.ai && i.human ? ' (human-only)' : ''),
        }))
      : [];

    const organizationsOptions = organizationsList.map(({ id, name }) => ({
      label: name,
      value: String(id),
    }));

    organizationsOptions.length && this.addNoneSelectOption(organizationsOptions);
    const organizationRules = this.processOrganizationRules();
    const { isMFARequiredRule, isDownloadCallRecordingsRule } = organizationRules;

    const {
      isHumanTranscriptionDisabled,
      isAITranscriptionDisabled,

      isTranscriptAvailableLanguageRule,
      isAITranscriptAvailableLanguageRule,

      isHumanTranscriptionCheckboxChecked,
      isAITranscriptionCheckboxChecked,

      errorLangHuman,
      errorLangAI,
    } = this.processTrscRules();

    return (
      <FlexContainer vertical>
        <H2>
          <FormattedMessage {...messages.generalInformation} />
        </H2>
        <div>
          <div>
            {conferenceHasBeenFinished && (
              <FlexContainer baseline>
                <FileUpload
                  id="audio-file-upload-input"
                  audio
                  setFilesToUpload={this.setCallAudioFile}
                  caption={
                    <FlexContainer inline gap="5px">
                      <Icon name="plusWhite" /> Add file
                    </FlexContainer>
                  }
                />
                <FileList
                  token={token}
                  files={this.mapFileNames(this.getCallAudioFiles())}
                />
              </FlexContainer>
            )}
            <Whitespace size="1rem" />
            <FlexContainer>
              <TimeManager {...this.props} />
            </FlexContainer>
            <Whitespace size="1.5rem" />
            <FlexContainer gap="1rem">
              <FlexContainer vertical style={{ minWidth: '300px' }}>
                <TextInput
                  underlined
                  id="transcript-title"
                  label={<FormattedMessage {...messages.transcriptTitle} />}
                  value={title}
                  onChange={this.handleTitleChange}
                />
                <Whitespace size="1rem" />
                <TextInput
                  underlined
                  id="client-reference"
                  label={<FormattedMessage {...messages.clientReference} />}
                  value={clientReference}
                  onChange={this.handleClientReferenceChange}
                  style={{ marginTop: '26px' }}
                />

                {!conferenceHasBeenFinished && (
                  <Fragment>
                    <Whitespace size="1rem" />
                    <TextInput
                      underlined
                      id="project-code"
                      label={<FormattedMessage {...messages.projectCode} />}
                      value={projectCode}
                      onChange={this.handleProjectCodeChange}
                      style={{ marginTop: '26px' }}
                    />
                    <Whitespace size="1rem" />
                    <TextInput
                      underlined
                      id="specialist-number"
                      label={<FormattedMessage {...messages.specialistNumber} />}
                      value={specialistNumber}
                      onChange={this.handleSpecialistNumberChange}
                      style={{ marginTop: '26px' }}
                    />
                    <Whitespace size="1rem" />
                  </Fragment>
                )}

                {!conferenceHasBeenFinished && (
                  <Fragment>
                    <Whitespace size="1rem" />
                    <IntroMessagesDropdown
                      id="intro-message-select"
                      introMessages={introMessagesListOptions}
                      value={introMessageId}
                      onChange={this.changeIntroMessage}
                      disabled={introMessageSelectDisabled}
                    />
                    <Whitespace size="1rem" />
                  </Fragment>
                )}
              </FlexContainer>

              <FlexContainer vertical style={{ minWidth: '300px' }}>
                <div>
                  <StyledLabel>
                    <FormattedMessage {...messages.client} />
                  </StyledLabel>
                  <Dropdown
                    id="organization-select"
                    options={organizationsOptions}
                    value={organizationId}
                    onChange={this.changeOrganization}
                  />
                </div>
                <Whitespace size="1rem" />
                <ChipsInput
                  id="tags"
                  floatingLabelText={<FormattedMessage {...messages.tags} />}
                  chips={tags}
                  addChip={addTag}
                  deleteChip={deleteTag}
                />
                <Whitespace size="1rem" />
                <LanguageDropdown
                  data={languagesListExt}
                  value={language}
                  onChange={this.changeLanguage}
                  disabled={false}
                />

                {errorLangHuman || errorLangAI ? (
                  <StyledError>
                    Selected language doesn't support chosen transcription mechanisms (see
                    below)
                  </StyledError>
                ) : null}
              </FlexContainer>
              <FlexContainer />
            </FlexContainer>
          </div>
          {edit && (
            <div>
              <Pin pin={pin} regeneratePin={regeneratePin} loading={isRegenerating} />
              <Whitespace size="1rem" />
              <ChangeAnalyst analystId={userId} changeAnalyst={this.changeAnalyst} />
            </div>
          )}
        </div>
        <Whitespace size="1rem" />
        <Fragment>
          <Whitespace size="1rem" />
          <People
            addPerson={addPerson}
            setPersonField={setPersonField}
            removePerson={removePerson}
            people={[...people, ...defaultAttendees]}
            numbersByCountries={numbersByCountries}
            conferenceHasBeenFinished={conferenceHasBeenFinished}
            header={<FormattedMessage {...messages.people} />}
          />
          <Whitespace size="1.5rem" />
          <Fragment>
            {!isTranscriptAvailableLanguageRule && isTranscriptNeeded ? (
              <StyledError>
                Selected transcription mechanism is not supported for the chosen language
              </StyledError>
            ) : null}
            <CheckboxBlock
              id="create-transcription"
              disabled={isHumanTranscriptionDisabled}
              label={<FormattedMessage {...messages.createHumanTranscription} />}
              checked={isHumanTranscriptionCheckboxChecked}
              onCheck={this.handleCheckCreateTranscript}
            />

            <Whitespace size="1rem" />
            {!isAITranscriptAvailableLanguageRule && isAITranscriptNeeded ? (
              <StyledError>
                Selected transcription mechanism is not supported for the chosen language
              </StyledError>
            ) : null}
            <CheckboxBlock
              id="create-ai-transcription"
              disabled={isAITranscriptionDisabled}
              label={<FormattedMessage {...messages.createAITranscription} />}
              checked={isAITranscriptionCheckboxChecked}
              onCheck={this.handleCheckCreateAITranscript}
            />

            <Whitespace size="1rem" />
          </Fragment>
          <CheckboxBlock
            id="request-two-factor-auth"
            disabled={this.isTwoFactorAuthDisabled(organizationRules)}
            label={<FormattedMessage {...messages.useTwoFactorAuth} />}
            checked={this.isTwoFactorAuthSelected(isMFARequiredRule)}
            onCheck={this.handleUsingTwoFactorAuth}
          />
          <Whitespace size="1rem" />
          <CheckboxBlock
            id="allow-audiofile-download-rule"
            disabled
            label={<FormattedMessage {...messages.allowAudioDownload} />}
            checked={isDownloadCallRecordingsRule}
          />
          <Whitespace size="1rem" />
        </Fragment>
      </FlexContainer>
    );
  }
}

ConferenceCallInformationForm.propTypes = {
  addPerson: PropTypes.func.isRequired,
  callAudio: PropTypes.object,
  conferenceCallUuid: PropTypes.string,
  date: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  errors: PropTypes.shape({
    title: PropTypes.string,
  }),
  people: PropTypes.arrayOf(personType),
  defaultAttendees: PropTypes.array,
  setCallAudio: PropTypes.func.isRequired,
  setPersonField: PropTypes.func.isRequired,
  title: PropTypes.string,
  token: PropTypes.string.isRequired,
  setField: PropTypes.func.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  addTag: PropTypes.func.isRequired,
  deleteTag: PropTypes.func.isRequired,
  conferenceHasBeenFinished: PropTypes.bool,
  pin: PropTypes.string,
  regeneratePin: PropTypes.func.isRequired,
  isRegenerating: PropTypes.bool.isRequired,
  userId: PropTypes.number,
  introMessageId: PropTypes.string,
  introMessagesList: PropTypes.array.isRequired,
  organizationId: PropTypes.string,
  organization: PropTypes.object,
  organizationsList: PropTypes.array.isRequired,
  loadOrganizationsList: PropTypes.func,
  getIntroMessagesList: PropTypes.func,
};

ConferenceCallInformationForm.defaultProps = {
  title: '',
  conferenceHasBeenFinished: false,
};

export default ConferenceCallInformationForm;



// WEBPACK FOOTER //
// ./components/templates/ConferenceCallInformationForm/ConferenceCallInformationForm.js